import { useEffect, useMemo, useState } from "react";
import { Modal, Input, Spin, Slider } from "antd";

// Simple modal that previews fonts from public/Fonts and lets the user pick one
export default function FontSignatureDialog({
	visible,
	onClose,
	onConfirm,
	defaultText = "Your Signature",
}) {
	const [previewText, setPreviewText] = useState(defaultText);
	const [loading, setLoading] = useState(false);
	const [selectedFont, setSelectedFont] = useState(null);
	const [detailSize, setDetailSize] = useState(38);
	const [fontQuery, setFontQuery] = useState("");

	// Declare the available fonts and their public URLs
	const runtimeFonts = (typeof window !== 'undefined' && Array.isArray(window.__SMVRT_SIGN_FONTS)) ? window.__SMVRT_SIGN_FONTS : null;
	const fonts = useMemo(
		() => (runtimeFonts && runtimeFonts.length > 0 ? runtimeFonts : [
			{ key: "AAutoSignature", label: "AAutoSignature", url: "/Fonts/AAutoSignature-1GD9j.ttf" },
			{ key: "AutografPersonalUseOnly", label: "Autograf", url: "/Fonts/AutografPersonalUseOnly-mOBm.ttf" },
			{ key: "Autography", label: "Autography", url: "/Fonts/Autography-DOLnW.otf" },
			{ key: "BrittanySignatureLjyZ", label: "Brittany Signature OTF", url: "/Fonts/BrittanySignature-LjyZ.otf" },
			{ key: "BrittanySignatureMaZx", label: "Brittany Signature TTF", url: "/Fonts/BrittanySignature-MaZx.ttf" },
			{ key: "DarlingtonDemo", label: "Darlington Demo", url: "/Fonts/DarlingtonDemo-z8xjG.ttf" },
			{ key: "JustSignature", label: "Just Signature", url: "/Fonts/JustSignature-92w7.ttf" },
			{ key: "ThePrestigeSignature", label: "The Prestige Signature", url: "/Fonts/Theprestigesignature-ywwaM.otf" },
			{ key: "BrightSunshineDemo", label: "Bright Sunshine Demo", url: "/Fonts/BrightSunshineDemo-7BvgD.ttf" },
			{ key: "HousttelySignature", label: "Housttely Signature", url: "/Fonts/HousttelySignature-GOonZ.ttf" },
			{ key: "AmericanSignature", label: "American Signature", url: "/Fonts/AmericanSignature-w14En.ttf" },
			{ key: "AnticallyRegular", label: "Antically Regular", url: "/Fonts/AnticallyRegular-OVox8.ttf" },
			{ key: "Motterdam", label: "Motterdam TTF", url: "/Fonts/Motterdam-K74zp.ttf" },
			{ key: "MotterdamOTF", label: "Motterdam OTF", url: "/Fonts/Motterdam-MVanr.otf" },
			{ key: "BrotherSignature", label: "Brother Signature", url: "/Fonts/BrotherSignature-7BWnK.otf" },
			{ key: "MillionAstteroids", label: "Million Astteroids", url: "/Fonts/MillionAstteroids-R9njo.ttf" },
			{ key: "SignericaFat", label: "Signerica Fat", url: "/Fonts/SignericaFat-1XOe.ttf" },
			{ key: "SignericaMedium", label: "Signerica Medium", url: "/Fonts/SignericaMedium-RXOo.ttf" },
			{ key: "SignericaThin", label: "Signerica Thin", url: "/Fonts/SignericaThin-BXv3.ttf" },
			{ key: "SevenDaySignature", label: "Seven Day Signature", url: "/Fonts/SevenDaySignature-RpKO3.otf" },
		]),
		[runtimeFonts]
	);

	useEffect(() => {
		if (!visible) return;
		let cancelled = false;
		async function ensureFontsInjectedAndLoaded() {
			setLoading(true);
			try {
				fonts.forEach((f) => {
					const styleId = `font-face-${f.key}`;
					if (!document.getElementById(styleId)) {
						const style = document.createElement('style');
						style.id = styleId;
						const isOtf = f.url.toLowerCase().endsWith('.otf');
						const format = isOtf ? 'opentype' : 'truetype';
						style.textContent = `@font-face { font-family: '${f.key}'; src: url('${f.url}') format('${format}'); font-display: swap; }`;
						document.head.appendChild(style);
					}
				});
				// Trigger load for each font family
				await Promise.all(
					fonts.map((f) => {
						try {
							return document.fonts.load(`48px '${f.key}'`, previewText || 'Your Signature');
						} catch {
							return Promise.resolve();
						}
					})
				);
				if (!cancelled) setLoading(false);
			} catch {
				if (!cancelled) setLoading(false);
			}
		}
		ensureFontsInjectedAndLoaded();
		return () => {
			cancelled = true;
		};
	}, [visible, fonts, previewText]);

	const handleOk = () => {
		if (!selectedFont) return;
		onConfirm && onConfirm({ text: previewText, fontFamily: selectedFont.key, fontUrl: selectedFont.url, fontSizePx: detailSize });
	};

	const filteredFonts = useMemo(() => {
		const q = fontQuery.trim().toLowerCase();
		if (!q) return fonts;
		return fonts.filter((f) => f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q));
	}, [fonts, fontQuery]);

	return (
		<Modal
			open={visible}
			title="Choose a Signature Style"
			onCancel={onClose}
			onOk={handleOk}
			okButtonProps={{ disabled: !selectedFont }}
			className="addSignatureDialog"
			width={1100}
			style={{ top: 20, zIndex: 9999, marginTop: '30px' }}
		>
			<div style={{ display: 'flex', gap: 16, minHeight: 480 }}>
				<div style={{ flex: 1 }}>
					<div style={{ display: 'flex', gap: 8, marginBottom: 12, paddingRight: '5px' }}>
						<div style={{ flex: 1 }}>
							<div style={{ marginBottom: 6, color: "#6F6F6F" }}>Type your name or signature text</div>
							<Input value={previewText} size="large" onChange={(e) => setPreviewText(e.target.value)} placeholder="Your Signature" />
						</div>
						<div style={{ width: 300 }}>
							<div style={{ marginBottom: 6, color: "#6F6F6F" }}>Search fonts</div>
							<Input value={fontQuery} size="large" onChange={(e) => setFontQuery(e.target.value)} placeholder="Search by name" allowClear />
						</div>
					</div>

					{loading ? (
						<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 520 }}>
							<Spin />
						</div>
					) : (
						<div style={{ maxHeight: 520, overflow: 'auto', paddingRight: 4 }}>
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
								{filteredFonts.map((f) => (
									<button
										key={f.key}
										onClick={() => setSelectedFont(f)}
										style={{ border: selectedFont?.key === f.key ? '2px solid #3265E2' : '1px solid #e5e7eb', borderRadius: 8, padding: 12, cursor: 'pointer', textAlign: 'left', background: '#fff' }}
									>
										<div style={{ color: '#374151', fontSize: 12, marginBottom: 8 }}>{f.label}</div>
										<div style={{ fontFamily: `'${f.key}'`, fontSize: 38, lineHeight: 1.1, color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingTop: '10px', paddingBottom: '10px' }}>
											{previewText || 'Your Signature'}
										</div>
									</button>
								))}
							</div>
						</div>
					)}
				</div>

				<div style={{ width: 420, borderLeft: '1px solid #e5e7eb', paddingLeft: 16 }}>
					<div style={{ color: '#6F6F6F', marginBottom: 8 }}>Detailed preview</div>
					<div style={{ marginBottom: 8, color: '#111827' }}>{selectedFont ? selectedFont.label : 'Select a font'}</div>
					{/* <div style={{ marginBottom: 8 }}>
						<div style={{ marginBottom: 6, color: '#6F6F6F' }}>Size</div>
						<Slider min={32} max={220} value={detailSize} onChange={setDetailSize} />
					</div> */}
					<div style={{ fontFamily: selectedFont ? `'${selectedFont.key}'` : 'inherit', fontSize: detailSize, lineHeight: 1.1, border: '1px dashed #e5e7eb', borderRadius: 8, padding: 12, paddingTop: '30px', minHeight: 160, color: '#111827', background: '#fff' }}>
						{previewText || 'Your Signature'}
					</div>
				</div>
			</div>
		</Modal>
	);
}

